import React, { useCallback, useEffect, useState } from 'react';
import { courseSupportingDataService, CourseSupportingDataItem } from '../../../../services/courseSupportingDataService';
import { CefrRegistryEditor } from './CefrRegistryEditor';
import { CourseImageModelsEditor } from './CourseImageModelsEditor';
import { CourseTextEditor } from './CourseTextEditor';
import { SummaryEditor } from './SummaryEditor';

export function CourseSharedResources() {
  const [assets, setAssets] = useState<CourseSupportingDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await courseSupportingDataService.list();
        setAssets(data.assets);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectedAsset = assets.find((a) => a.asset_id === selectedAssetId);

  // Route to specific editor by editor_type
  if (selectedAsset) {
    if (selectedAsset.editor_type === 'cefr_registry') {
      return (
        <div className="p-6 max-w-4xl">
          <button
            onClick={() => setSelectedAssetId(null)}
            className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-block"
          >
            &larr; Back to supporting data
          </button>
          <CefrEditorWithSummary asset={selectedAsset} />
        </div>
      );
    }
    if (selectedAsset.editor_type === 'image_models') {
      return (
        <div className="p-6 max-w-4xl">
          <button
            onClick={() => setSelectedAssetId(null)}
            className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-block"
          >
            &larr; Back to supporting data
          </button>
          <CourseImageModelsEditor item={selectedAsset} />
        </div>
      );
    }
    // Generic markdown/text editor for other types
    return (
      <div className="p-6 max-w-4xl">
        <button
          onClick={() => setSelectedAssetId(null)}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-block"
        >
          &larr; Back to supporting data
        </button>
        <CourseTextEditor item={selectedAsset} onBack={() => setSelectedAssetId(null)} />
      </div>
    );
  }

  if (loading) {
    return <div className="p-6 text-gray-400 text-sm">Loading shared resources...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Supporting Data</h2>

      <p className="text-sm text-gray-500 mb-6">
        Editable supporting documents used across all course generation stages.
      </p>

      <div className="space-y-2">
        {assets.map((asset) => (
          <div
            key={asset.asset_id}
            onClick={() => setSelectedAssetId(asset.asset_id)}
            className="bg-white border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-all"
            style={{ boxShadow: 'inset 0 0 0 0 #01bcd4' }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'inset 4px 0 0 0 #01bcd4'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'inset 0 0 0 0 #01bcd4'; }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 text-sm">{asset.name}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{asset.group}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{asset.description}</div>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CefrEditorWithSummary({ asset }: { asset: CourseSupportingDataItem }) {
  const [activeTab, setActiveTab] = useState<'full' | 'summary'>('full');

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{asset.name}</h2>
        <p className="text-sm text-gray-500 mt-1">{asset.description}</p>
      </div>

      {asset.has_summary && (
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('full')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'full'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Full Document
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'summary'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Summary (used by directional stages)
          </button>
        </div>
      )}

      {activeTab === 'full' ? (
        <CefrRegistryEditor />
      ) : (
        <SummaryEditor assetId={asset.asset_id} />
      )}
    </div>
  );
}
